import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
	ArrowLeftIcon,
	ArrowRightIcon,
	CheckIcon,
} from '@heroicons/react/24/outline';
import {
	getResource,
	resources,
	resourceCategories,
} from '../../content/resources';
import SunRaysHero from '../Services/SunRaysHero';

function Eyebrow({ children }) {
	return (
		<p className='text-xs font-semibold uppercase tracking-[0.18em] text-primary-600'>
			<span className='inline-block w-8 h-px align-middle mr-3 bg-current opacity-60' />
			{children}
		</p>
	);
}

// Renders one typed block from a resource article. Keeping this in one place
// means adding an article is pure data — no new components.
function Block({ block }) {
	switch (block.type) {
		case 'h':
			return (
				<h2 className='font-display mt-12 text-2xl font-medium tracking-tight text-ink-900'>
					{block.text}
				</h2>
			);
		case 'p':
			return (
				<p className='mt-5 text-lg leading-8 text-ink-600'>{block.text}</p>
			);
		case 'list':
			return (
				<ul className='mt-5 space-y-3'>
					{block.items.map((item) => (
						<li
							key={item}
							className='flex items-start gap-3'
						>
							<div className='mt-1 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary-100'>
								<CheckIcon
									className='h-3 w-3 text-primary-700'
									strokeWidth={3}
								/>
							</div>
							<span className='text-lg leading-7 text-ink-600'>{item}</span>
						</li>
					))}
				</ul>
			);
		case 'callout':
			return (
				<p className='mt-8 rounded-2xl border-l-2 border-primary-500 bg-surface px-6 py-5 font-display text-xl font-medium text-ink-900'>
					{block.text}
				</p>
			);
		case 'columns':
			return (
				<div className='mt-8 grid grid-cols-1 gap-6 md:grid-cols-2'>
					{block.columns.map((col) => (
						<div
							key={col.title}
							className='sg-card p-7'
						>
							<h3 className='font-display text-xl font-medium text-ink-900'>
								{col.title}
							</h3>
							{col.subtitle && (
								<p className='mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-ink-400'>
									{col.subtitle}
								</p>
							)}
							<ul className='mt-3 space-y-2'>
								{col.items.map((item) => (
									<li
										key={item}
										className='flex items-start gap-2 text-sm leading-6 text-ink-600'
									>
										<CheckIcon className='mt-1 h-3.5 w-3.5 flex-none text-primary-500' />
										{item}
									</li>
								))}
							</ul>
							{col.advantages && (
								<>
									<p className='mt-5 text-xs font-semibold uppercase tracking-[0.14em] text-ink-400'>
										Advantages
									</p>
									<ul className='mt-3 space-y-2'>
										{col.advantages.map((item) => (
											<li
												key={item}
												className='flex items-start gap-2 text-sm leading-6 text-ink-600'
											>
												<CheckIcon className='mt-1 h-3.5 w-3.5 flex-none text-primary-500' />
												{item}
											</li>
										))}
									</ul>
								</>
							)}
						</div>
					))}
				</div>
			);
		default:
			return null;
	}
}

export default function ResourceArticle() {
	const { resourceId } = useParams();
	const article = getResource(resourceId);

	if (!article)
		return (
			<Navigate
				to='/resources'
				replace
			/>
		);

	const related = resources
		.filter((r) => r.id !== article.id && r.category === article.category)
		.slice(0, 3);

	return (
		<div className='bg-surface text-ink-900 antialiased'>
			<section className='relative isolate overflow-hidden border-b border-ink-100'>
				<SunRaysHero seed={9} />
				<div className='mx-auto max-w-3xl px-6 py-16 sm:py-20 lg:px-8'>
					<Link
						to='/resources'
						className='inline-flex items-center gap-2 text-sm font-semibold text-ink-500 hover:text-ink-900'
					>
						<ArrowLeftIcon className='h-4 w-4' />
						Resource center
					</Link>
					<div className='mt-8'>
						<Eyebrow>{resourceCategories[article.category]}</Eyebrow>
						<h1 className='font-display mt-5 text-4xl font-medium tracking-tight text-ink-900 sm:text-5xl leading-[1.08]'>
							{article.title}
						</h1>
						{article.readingTime && (
							<p className='mt-4 text-sm text-ink-400'>
								{article.readingTime} read
							</p>
						)}
					</div>
				</div>
			</section>

			<article className='bg-white py-16 sm:py-20'>
				<div className='mx-auto max-w-3xl px-6 lg:px-8'>
					{article.blocks.map((block, i) => (
						<Block
							key={i}
							block={block}
						/>
					))}

					<div className='mt-14 rounded-3xl bg-ink-900 px-8 py-10 text-center'>
						<h2 className='font-display text-2xl font-medium text-white'>
							Have a question about your home?
						</h2>
						<p className='mx-auto mt-3 max-w-md text-sm leading-6 text-white/70'>
							Every home is different. Tell me about yours and I&apos;ll give you
							a straight answer.
						</p>
						<div className='mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-3'>
							<Link
								to='/ask-gabe'
								className='inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary-500/20 hover:bg-primary-600 transition-all'
							>
								Ask Gabe a question
								<ArrowRightIcon className='h-4 w-4' />
							</Link>
							<Link
								to='/contact'
								className='text-sm font-semibold text-white/85 hover:text-white'
							>
								or schedule an assessment
							</Link>
						</div>
					</div>
				</div>
			</article>

			{related.length > 0 && (
				<section className='bg-surface border-t border-ink-100 py-16'>
					<div className='mx-auto max-w-7xl px-6 lg:px-8'>
						<h2 className='font-display text-2xl font-medium tracking-tight text-ink-900'>
							Related reading
						</h2>
						<div className='mt-8 grid grid-cols-1 gap-4 md:grid-cols-3'>
							{related.map((r) => (
								<Link
									key={r.id}
									to={`/resources/${r.id}`}
									className='group sg-card sg-card-interactive p-6'
								>
									<h3 className='font-display text-lg font-medium text-ink-900'>
										{r.title}
									</h3>
									<p className='mt-3 text-sm leading-6 text-ink-500'>
										{r.excerpt}
									</p>
								</Link>
							))}
						</div>
					</div>
				</section>
			)}
		</div>
	);
}
